// src/pages/UserMeet/MeetRedirect.jsx
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Video,
  ArrowRight,
  Users,
  Shield,
  Clock,
  Sparkles,
  Copy,
  Check,
} from "lucide-react";

export default function MeetRedirect() {
  const { joinCode: paramJoinCode } = useParams();

  // ✅ Also extract from window.location as fallback
  const pathname = window.location.pathname;
  const match = pathname.match(/^\/meet\/(.+)$/);
  const joinCode = paramJoinCode || (match ? match[1] : null);

  const joinUrl = joinCode ? `/user-meet/join/${joinCode}` : "/user-meet";

  const fullLink = joinCode
    ? `${window.location.origin}/user-meet/join/${joinCode}`
    : "";

  const [copied, setCopied] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [autoRedirectCancelled, setAutoRedirectCancelled] = useState(false);

  // ✅ Auto-redirect countdown
  useEffect(() => {
    if (autoRedirectCancelled || !joinCode) return;

    if (countdown <= 0) {
      window.location.href = joinUrl;
      return;
    }

    const timer = setTimeout(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, autoRedirectCancelled, joinCode, joinUrl]);

  // ✅ Copy link
  const handleCopy = () => {
    navigator.clipboard.writeText(fullLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ✅ Join now
  const handleJoin = () => {
    window.location.href = joinUrl;
  };

  // ✅ Cancel auto redirect
  const cancelAutoRedirect = () => {
    setAutoRedirectCancelled(true);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
      }}
    >
      {/* Background glow */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 30%, rgba(198,168,124,0.08) 0%, transparent 60%)",
        }}
      />

      <div className="relative max-w-lg w-full text-center">
        {/* ── Logo / Brand ── */}
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-[#C6A87C]/10 border border-[#C6A87C]/20 mb-6">
            <Video size={36} className="text-[#C6A87C]" />
          </div>

          <h1
            className="text-4xl md:text-5xl font-light tracking-tighter text-white mb-2"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            <span className="italic text-[#C6A87C]">vmeet</span> {" "}io
          </h1>

          <p className="text-white/40 text-sm">
            You've been invited to a meeting
          </p>
        </div>

        {/* ── Meeting Card ── */}
        <div
          className="rounded-2xl border border-white/[0.08] overflow-hidden mb-6"
          style={{ background: "rgba(255,255,255,0.03)" }}
        >
          {/* Join code display */}
          <div className="px-6 py-5 border-b border-white/[0.06]">
            <p className="text-[10px] font-semibold tracking-widest uppercase text-white/30 mb-3">
              Meeting Code
            </p>
            <div className="flex items-center justify-center gap-3">
              <span
                className="text-2xl font-mono tracking-[0.2em] text-[#C6A87C] font-medium"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                {joinCode || "—"}
              </span>
              {joinCode && (
                <button
                  onClick={handleCopy}
                  className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#C6A87C]/10 hover:border-[#C6A87C]/30 transition-all"
                  title="Copy meeting code"
                >
                  {copied ? (
                    <Check size={14} className="text-green-400" />
                  ) : (
                    <Copy size={14} className="text-white/40" />
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Features */}
          <div className="px-6 py-5 border-b border-white/[0.06]">
            <div className="grid grid-cols-3 gap-4">
              {[
                { icon: Users, label: "Video & Audio", desc: "HD quality" },
                { icon: Shield, label: "Encrypted", desc: "End-to-end" },
                { icon: Clock, label: "Instant", desc: "No downloads" },
              ].map((feature) => {
                const Icon = feature.icon;
                return (
                  <div key={feature.label} className="text-center">
                    <div className="w-10 h-10 rounded-xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center mx-auto mb-2">
                      <Icon size={16} className="text-[#C6A87C]/70" />
                    </div>
                    <p className="text-xs font-medium text-white/60">
                      {feature.label}
                    </p>
                    <p className="text-[10px] text-white/30">{feature.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Join Button */}
          <div className="px-6 py-6">
            <button
              onClick={handleJoin}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl bg-[#C6A87C] text-[#061412] font-semibold text-base hover:bg-[#D9BC93] transition-all hover:scale-[1.02] active:scale-[0.98] group"
            >
              <Video size={20} />
              <span>Join Meeting Now</span>
              <ArrowRight
                size={18}
                className="group-hover:translate-x-1 transition-transform"
              />
            </button>

            {/* Auto-redirect countdown */}
            {!autoRedirectCancelled && joinCode && (
              <div className="mt-4 flex items-center justify-center gap-2">
                <p className="text-xs text-white/30">
                  Redirecting automatically in{" "}
                  <span className="text-[#C6A87C] font-semibold">
                    {countdown}s
                  </span>
                </p>
                <button
                  onClick={cancelAutoRedirect}
                  className="text-[10px] text-white/25 underline underline-offset-2 hover:text-white/50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}

            {autoRedirectCancelled && (
              <p className="mt-3 text-xs text-white/25">
                Click the button above to join when you're ready
              </p>
            )}
          </div>
        </div>

        {/* ── Alternative: Direct link ── */}
        {joinCode && (
          <div
            className="rounded-xl border border-white/[0.06] px-5 py-4 mb-6"
            style={{ background: "rgba(255,255,255,0.02)" }}
          >
            <p className="text-[10px] font-semibold tracking-widest uppercase text-white/25 mb-2">
              Or open this link directly
            </p>
            <a
              href={joinUrl}
              className="text-xs font-mono text-[#C6A87C]/70 hover:text-[#C6A87C] transition-colors break-all"
            >
              {fullLink}
            </a>
          </div>
        )}

        {/* ── Powered by ── */}
        <div className="flex items-center justify-center gap-2 text-white/15 text-xs">
          <Sparkles size={12} />
          <span>Powered by VMeetio</span>
        </div>

        {/* ── No code / error state ── */}
        {!joinCode && (
          <div className="mt-8 p-5 rounded-2xl border border-red-500/20 bg-red-500/[0.06]">
            <p className="text-sm text-red-400 font-medium mb-2">
              Invalid meeting link
            </p>
            <p className="text-xs text-white/40 mb-4">
              This link doesn't contain a valid meeting code. Please check the
              link and try again.
            </p>
            <a
              href="/user-meet"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white/60 text-sm hover:border-white/25 hover:text-white transition-all"
            >
              Go to Meetings
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
